import React from 'react';
import clsx from 'clsx';
import { useDateFormatter } from '@react-aria/i18n';

import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';

import { chunks, isMonthDisabled, range, sentenceCase } from './utils';
import { CalendarMonthOrYearCell } from './CalendarMonthOrYearCell';
import { CalendarState } from './useCalendarState';
import { useCustomDurationCalendarGrid } from './useCustomDurationCalendarGrid';

interface CalendarMonthsGridProps<T extends string> {
  state: CalendarState<T>;
}

export function CalendarMonthsGrid<T extends string>({
  state,
}: CalendarMonthsGridProps<T>): JSX.Element {
  const formatter = useDateFormatter({
    month: 'long',
    timeZone: state.timeZone,
  });

  const numMonths = state.focusedDate.calendar.getMonthsInYear(
    state.focusedDate
  );
  const months = range(1, numMonths).map((month) =>
    formatter.format(state.focusedDate.set({ month }).toDate(state.timeZone))
  );
  const monthRows = [...chunks(months, 3)];

  const { gridProps } = useCustomDurationCalendarGrid(
    { cellsDuration: 'months' },
    state
  );

  return (
    <table
      className={clsx(
        styles['sapphire-calendar__table'],
        styles['sapphire-calendar__table--month-year']
      )}
      {...gridProps}
    >
      <tbody>
        {monthRows.map((currentRowMonths, rowIndex) => (
          <tr
            key={rowIndex}
            className={styles['sapphire-calendar__table-body-row']}
          >
            {currentRowMonths.map((month, index) => {
              const newFocusedDate = state.focusedDate.set({
                month: months.indexOf(month) + 1,
              });

              const isDisabled = isMonthDisabled(newFocusedDate, state);

              return (
                <CalendarMonthOrYearCell
                  key={index}
                  date={newFocusedDate}
                  field={'month'}
                  isDisabled={isDisabled}
                  state={state}
                >
                  {sentenceCase(month)}
                </CalendarMonthOrYearCell>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
